import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Send } from 'lucide-react';
import { messagesApi } from '@/lib/api';
import { Conversation, UserProfile, Message } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface ChatPanelProps {
  conversation: Conversation | null;
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile;
}

export const ChatPanel = ({ conversation, isOpen, onClose, currentUser }: ChatPanelProps) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const prevConversationId = useRef<string | null>(null);

  // Fetch messages - refetch more frequently when open
  const { data: messages = [], isLoading, refetch } = useQuery({
    queryKey: ['messages', conversation?.id],
    queryFn: () => conversation ? messagesApi.getMessages(conversation.id) : Promise.resolve([]),
    enabled: !!conversation,
    refetchInterval: 3000, // Poll every 3 seconds for new messages
  });

  // Refetch messages when conversation changes or panel opens
  useEffect(() => {
    if (isOpen && conversation && conversation.id !== prevConversationId.current) {
      prevConversationId.current = conversation.id;
      refetch();
    }
  }, [isOpen, conversation?.id, refetch]);

  const sendMessageMutation = useMutation({
    mutationFn: (text: string) => {
      if (!conversation) throw new Error('No conversation');
      return messagesApi.sendMessage(conversation.id, { text });
    },
    onSuccess: () => {
      setNewMessage('');
      // Immediately refetch messages and conversations
      queryClient.invalidateQueries({ queryKey: ['messages', conversation?.id] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // Also do an immediate refetch for better UX
      refetch();
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!isOpen || !conversation) return null;

  const otherUser = conversation.participants.find(p => p.id !== currentUser.id);
  const otherUserName = otherUser?.name || 'Unknown User';
  const otherUserInitials = otherUserName
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    sendMessageMutation.mutate(newMessage);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-background w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col h-[80vh] sm:h-[600px]">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={otherUser?.avatar} alt={otherUserName} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {otherUserInitials || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{otherUserName}</p>
              <p className="text-xs text-muted-foreground">{conversation.ride?.destinationName || 'Direct message'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Start a conversation</p>
          ) : (
            messages.map((msg: Message) => (
              <div
                key={msg.id}
                className={`flex ${msg.senderId === currentUser.id ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    msg.senderId === currentUser.id
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted rounded-bl-md'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className="text-[10px] opacity-70 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSend} className="p-4 border-t flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-muted rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sendMessageMutation.isPending}
            className="p-2 bg-primary text-primary-foreground rounded-full hover:opacity-90 disabled:opacity-50"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPanel;
